import { useEffect, useState, useCallback } from "react";
import { useHub } from "../../context/HubContext";
import { getTicketBuyers } from "../../services/attendeeService";
import { getHubEvents } from "../../services/eventService";
import { formatDate, formatDateTime } from "../../utils/formatters";
import PageHeader from "../../components/common/PageHeader";
import Avatar from "../../components/common/Avatar";
import Badge from "../../components/common/Badge";
import {
  Search,
  ChevronLeft,
  ChevronRight,
  Users,
  CheckCircle2,
  Clock,
  ChevronDown,
  ChevronUp,
  Calendar,
} from "lucide-react";

const AttendeeRow = ({ buyer }) => {
  const [expanded, setExpanded] = useState(false);
  const extras = buyer.extras?.$values ?? buyer.extras ?? [];
  const hasExtras = extras.length > 0;

  return (
    <div className="border-b border-border last:border-0">
      <div
        className={`grid grid-cols-[2fr_1.5fr_1fr_1fr_1fr] gap-4 items-center px-5 py-3 hover:bg-bg-elevated transition ${
          hasExtras ? "cursor-pointer" : ""
        }`}
        onClick={() => hasExtras && setExpanded((p) => !p)}
      >
        {/* Attendee */}
        <div className="flex items-center gap-3 min-w-0">
          <Avatar
            name={buyer.user?.name}
            surname={buyer.user?.surname}
            image={buyer.user?.image}
            size="sm"
          />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm text-text-primary font-medium truncate">
                {buyer.user?.name} {buyer.user?.surname}
              </p>
              {hasExtras && (
                <span className="flex-shrink-0 text-xs bg-secondary/10 text-secondary border border-secondary/20 px-2 py-0.5 rounded-full font-medium">
                  +{extras.length} extra{extras.length !== 1 ? "s" : ""}
                </span>
              )}
            </div>
            <p className="text-xs text-text-muted truncate">
              {buyer.user?.email}
            </p>
          </div>
        </div>

        {/* Event */}
        <div className="flex items-center gap-1.5 min-w-0">
          <Calendar size={11} className="text-primary flex-shrink-0" />
          <span className="text-xs text-text-muted truncate">
            {buyer.event?.title ?? "—"}
          </span>
        </div>

        {/* Ticket */}
        <div className="text-xs text-text-muted truncate">
          {buyer.ticketName ?? "—"}
        </div>

        {/* Scanned At */}
        <div className="text-xs text-text-muted">
          {buyer.scannedAt ? formatDateTime(buyer.scannedAt) : "—"}
        </div>

        {/* Status */}
        <div className="flex items-center justify-end gap-2">
          {buyer.isScanned ? (
            <span className="flex items-center gap-1 text-xs text-primary font-medium">
              <CheckCircle2 size={12} /> Scanned
            </span>
          ) : (
            <span className="flex items-center gap-1 text-xs text-text-muted">
              <Clock size={12} /> Pending
            </span>
          )}
          {hasExtras && (
            <span className="text-text-muted">
              {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            </span>
          )}
        </div>
      </div>

      {/* Expanded extras */}
      {expanded && hasExtras && (
        <div className="px-5 pb-3 pt-2 bg-bg-elevated border-t border-border">
          <p className="text-xs text-text-muted mb-2">Extras purchased</p>
          <div className="flex flex-wrap gap-2">
            {extras.map((extra, i) => (
              <span
                key={i}
                className="text-xs bg-secondary/10 text-secondary border border-secondary/20 px-2.5 py-0.5 rounded-full"
              >
                {extra.eventExtra?.name ?? "Extra"}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const Attendees = () => {
  const { hub } = useHub();
  const [buyers, setBuyers] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ totalRecords: 0, totalPages: 0 });
  const [selectedEventId, setSelectedEventId] = useState("");
  const [scannedFilter, setScannedFilter] = useState(""); // "" | "scanned" | "pending"

  // Load events for filter dropdown
  useEffect(() => {
    if (!hub?.id) return;
    const fetch = async () => {
      try {
        const res = await getHubEvents(hub.id, { pageSize: 100 });
        setEvents(res.data.filter(Boolean));
      } catch {
        // fail silently
      }
    };
    fetch();
  }, [hub?.id]);

  const fetchBuyers = useCallback(
    async (searchVal, pageVal, eventId) => {
      if (!hub?.id) return;
      setLoading(true);
      try {
        const res = await getTicketBuyers({
          hubId: hub.id,
          eventId: eventId || undefined,
          search: searchVal,
          pageNumber: pageVal,
          pageSize: 20,
        });

        // Apply scanned filter client-side
        const filtered =
          scannedFilter === "scanned"
            ? res.data.filter((b) => b.isScanned)
            : scannedFilter === "pending"
              ? res.data.filter((b) => !b.isScanned)
              : res.data;

        setBuyers(filtered);
        setMeta({ totalRecords: res.totalRecords, totalPages: res.totalPages });
      } finally {
        setLoading(false);
      }
    },
    [hub?.id, scannedFilter],
  );

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      fetchBuyers(search, 1, selectedEventId);
    }, 400);
    return () => clearTimeout(t);
  }, [search, selectedEventId, scannedFilter, hub?.id]);

  useEffect(() => {
    fetchBuyers(search, page, selectedEventId);
  }, [page]);

  const scannedCount = buyers.filter((b) => b.isScanned).length;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Attendees"
        subtitle={`${meta.totalRecords.toLocaleString()} ticket buyers`}
      />

      {/* Scanned summary */}
      {buyers.length > 0 && (
        <div className="grid grid-cols-3 gap-4">
          {[
            {
              label: "Total Buyers",
              value: buyers.length,
              color: "text-text-primary",
              bg: "bg-bg-surface",
            },
            {
              label: "Scanned In",
              value: scannedCount,
              color: "text-primary",
              bg: "bg-primary/5 border-primary/20",
            },
            {
              label: "Pending",
              value: buyers.length - scannedCount,
              color: "text-text-muted",
              bg: "bg-bg-surface",
            },
          ].map((stat) => (
            <div
              key={stat.label}
              className={`border border-border rounded-2xl p-4 text-center ${stat.bg}`}
            >
              <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
              <p className="text-xs text-text-muted mt-0.5">{stat.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"
          />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm bg-bg-surface border border-border rounded-xl text-text-primary placeholder-text-hint focus:outline-none focus:border-primary transition"
          />
        </div>

        {/* Event filter */}
        <select
          value={selectedEventId}
          onChange={(e) => {
            setSelectedEventId(e.target.value);
            setPage(1);
          }}
          className="text-sm bg-bg-surface border border-border rounded-xl px-3 py-2 text-text-secondary focus:outline-none focus:border-primary transition"
        >
          <option value="">All events</option>
          {events.map((event) => (
            <option key={event.id} value={event.id}>
              {event.title}
            </option>
          ))}
        </select>

        {/* Scanned filter */}
        <div className="flex gap-1 bg-bg-surface border border-border rounded-xl p-1">
          {[
            { label: "All", value: "" },
            { label: "Scanned", value: "scanned" },
            { label: "Pending", value: "pending" },
          ].map((f) => (
            <button
              key={f.label}
              onClick={() => {
                setScannedFilter(f.value);
                setPage(1);
              }}
              className={`px-3 py-1 rounded-lg text-xs font-medium transition ${
                scannedFilter === f.value
                  ? "ingroove-gradient-bg text-white shadow-glow"
                  : "text-text-muted hover:text-text-secondary"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-bg-surface border border-border rounded-2xl overflow-hidden">
        {/* Table header */}
        {/* Table header */}
        <div className="grid grid-cols-[2fr_1.5fr_1fr_1fr_1fr] gap-4 px-5 py-3 bg-bg-elevated text-xs text-text-muted uppercase tracking-wide border-b border-border">
          <span>Attendee</span>
          <span>Event</span>
          <span>Ticket</span>
          <span>Scanned At</span>
          <span className="text-right">Status</span>
        </div>

        {loading ? (
          <div>
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="flex items-center gap-3 px-5 py-3 border-b border-border animate-pulse"
              >
                <div className="w-7 h-7 rounded-full bg-bg-elevated flex-shrink-0" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3 bg-bg-elevated rounded w-32" />
                  <div className="h-2.5 bg-bg-elevated rounded w-48" />
                </div>
                <div className="h-3 bg-bg-elevated rounded w-16" />
              </div>
            ))}
          </div>
        ) : buyers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <Users size={28} className="text-border-hover mb-2" />
            <p className="text-xs text-text-muted">No attendees found</p>
          </div>
        ) : (
          <div>
            {buyers.map((buyer) => (
              <AttendeeRow key={buyer.id} buyer={buyer} />
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {meta.totalPages > 1 && (
        <div className="flex items-center justify-between text-xs text-text-muted">
          <span>
            Page {page} of {meta.totalPages} —{" "}
            {meta.totalRecords.toLocaleString()} attendees
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded-xl border border-border hover:border-primary hover:text-primary transition disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={14} />
            </button>
            <button
              onClick={() => setPage((p) => Math.min(meta.totalPages, p + 1))}
              disabled={page === meta.totalPages}
              className="p-1.5 rounded-xl border border-border hover:border-primary hover:text-primary transition disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Attendees;
